import React from "react";
import WaveformPlayer from "./WaveformPlayer";

const DynamicSoundboardSection = ({
  description = "",
  timeline = [],
  audioFiles = [],
  notes = [],
  backgroundImage,
  optimizedContent = {},
  logoUrl,
}) => {
  // Use optimized description if available
  const optimizedDescription =
    optimizedContent["soundboard.description"]?.displayContent;
  // Notes: check for optimized content for each note
  const getNoteContent = (idx, note) => {
    const optKey = `soundboard.notes.${idx}`;
    return optimizedContent[optKey]?.displayContent || note.content;
  };

  // Process audio descriptions for backward compatibility
  const getAudioDescriptions = (audio) => {
    // If audio has a descriptions array, use it
    if (Array.isArray(audio.descriptions) && audio.descriptions.length > 0) {
      return audio.descriptions;
    }
    // Otherwise, use the single description if available
    return audio.description ? [audio.description] : [];
  };

  return (
    <section className="min-h-screen relative flex items-center justify-center py-20 overflow-hidden -mt-[20vh] z-10 bg-black/60">
      {/* Gradient overlay */}
      <div
        className="absolute inset-x-0 top-0 h-[30vh] pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, rgba(17, 17, 17, 0) 0%, rgba(17, 17, 17, 0.3) 40%, rgba(17, 17, 17, 0.5) 100%)",
          zIndex: -5,
        }}
      />
      {/* Dot pattern background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/15 to-black/20 opacity-50"></div>
      </div>
      {/* Background image (optional) */}
      {backgroundImage && (
        <div
          className="absolute inset-0 w-full h-full bg-center bg-cover bg-no-repeat"
          style={{
            backgroundImage: `url(${backgroundImage})`,
            opacity: 0.5,
            filter: "contrast(1.1) brightness(1.05)",
            mixBlendMode: "soft-light",
          }}
        />
      )}
      <div className="relative z-10 w-full mx-auto pt-[25vh]">
        {/* Title */}
        <div className="max-w-7xl mx-auto px-6 mb-20">
          <h2
            className="text-5xl md:text-7xl mb-8 font-bold text-white"
            style={{ textShadow: "0 2px 6px rgba(0,0,0,0.5)" }}
          >
            Brand Soundboard
          </h2>
        </div>
        {/* Overview/Description */}
        <section className="mb-32 min-h-[40vh] relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6">
            <h3 className="text-4xl md:text-5xl font-bold mb-12 text-white">
              Overview
            </h3>
            <div className="w-full md:w-4/5 lg:w-2/3 ml-0">
              <div className="prose prose-xl prose-invert max-w-3xl">
                <p className="text-lg md:text-xl leading-relaxed">
                  {optimizedDescription ||
                    description ||
                    "No description provided."}
                </p>
              </div>
            </div>
          </div>
        </section>
        {/* Timeline */}
        <section className="mb-32 min-h-[40vh] relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6">
            <h3 className="text-4xl md:text-5xl font-bold mb-16 text-white text-right">
              Strategy Timeline
            </h3>
            <div className="flex justify-center">
              <div className="w-full md:w-4/5 lg:w-3/4">
                <div className="relative">
                  {/* Timeline line */}
                  <div
                    className="absolute left-[30px] md:left-1/2 top-0 bottom-0 w-[3px] rounded-full md:transform md:-translate-x-1/2"
                    style={{
                      background:
                        "linear-gradient(to bottom, var(--primary-color) 0%, rgba(255,255,255,0.1) 100%)",
                    }}
                  ></div>
                  {timeline && timeline.length > 0 ? (
                    <div className="space-y-16">
                      {timeline.map((step, index) => (
                        <div
                          key={`timeline-step-${index}`}
                          className={`relative flex items-start ${
                            index % 2 === 0
                              ? "md:justify-start"
                              : "md:justify-end"
                          }`}
                        >
                          {/* Timeline marker */}
                          <div
                            className={`absolute top-3 w-[24px] h-[24px] rounded-full bg-white shadow-lg transform -translate-x-[10.5px] md:${
                              index % 2 === 0
                                ? "-translate-x-[10.5px]"
                                : "translate-x-[10.5px]"
                            } flex items-center justify-center z-10`}
                          >
                            <div className="w-[16px] h-[16px] rounded-full bg-[var(--primary-color)]"></div>
                          </div>
                          {/* Step content */}
                          <div
                            className={`w-full md:w-[calc(50%-30px)] pl-16 md:pl-0 md:${
                              index % 2 === 0
                                ? "pr-16 md:pr-8 text-right"
                                : "pl-16 md:pl-8 text-left"
                            }`}
                          >
                            <div
                              className={`text-sm font-bold py-1.5 px-5 rounded-full inline-block ${
                                index % 2 === 0 ? "md:ml-auto" : "md:ml-0"
                              }`}
                              style={{
                                backgroundColor: "var(--primary-color)",
                                color: "white",
                                boxShadow: "0 3px 10px rgba(0,0,0,0.3)",
                              }}
                            >
                              STEP {index + 1}
                            </div>
                            <h4
                              className={`text-2xl md:text-3xl font-semibold mb-5 text-white mt-8 ${
                                index % 2 === 0
                                  ? "md:text-right"
                                  : "md:text-left"
                              }`}
                            >
                              {step.title || `Step ${index + 1}`}
                            </h4>
                            <div
                              className={`prose prose-2xl prose-invert ${
                                index % 2 === 0
                                  ? "md:text-right"
                                  : "md:text-left"
                              }`}
                            >
                              <p className="text-xl md:text-2xl font-medium leading-relaxed text-white/90">
                                {step.textContent ||
                                  step ||
                                  "No content provided."}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="relative pl-20 min-h-[200px] flex items-center">
                      <div className="absolute left-[30px] md:left-1/2 top-1/2 w-[20px] h-[20px] rounded-full bg-white/50 shadow-glow transform -translate-x-1/2 -translate-y-1/2"></div>
                      <p className="text-white/60 italic text-xl">
                        No timeline steps added yet.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Audio Files */}
        <section className="min-h-[40vh] relative overflow-hidden py-20">
          <div className="max-w-7xl mx-auto px-6">
            <h3 className="text-4xl md:text-5xl font-bold mb-12 text-white">
              Sounds
            </h3>
            <div className="w-full md:w-4/5 lg:w-3/4 ml-0">
              {audioFiles && audioFiles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {audioFiles.map((audio, idx) => {
                    const mediaType = audio.mediaType || "audio";
                    return (
                      <div
                        key={`media-file-${idx}`}
                        className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 shadow-lg transition-all duration-300"
                      >
                        {mediaType === "video" ? (
                          <div className="space-y-4">
                            <h4 className="text-xl font-semibold text-white mb-2">
                              {audio.title || "Video"}
                            </h4>
                            <div className="w-full aspect-video rounded-lg overflow-hidden bg-black/20">
                              <video
                                controls
                                className="w-full h-full"
                                src={audio.url}
                              >
                                Your browser does not support the video tag.
                              </video>
                            </div>
                            {getAudioDescriptions(audio).length > 0 && (
                              <div className="space-y-2">
                                {getAudioDescriptions(audio).map((desc, descIdx) => (
                                  <p
                                    key={descIdx}
                                    className="text-white/70 text-sm whitespace-pre-wrap"
                                  >
                                    {desc}
                                  </p>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <WaveformPlayer
                            key={`waveform-${idx}-${audio.url}`}
                            url={audio.url}
                            title={audio.title || "Audio Track"}
                            subtitle={audio.description || ""}
                            descriptions={getAudioDescriptions(audio)}
                            artwork={audio.imageUrl || logoUrl || null}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-24 h-24 rounded-full bg-white/5 flex items-center justify-center mb-6">
                    <svg
                      className="w-16 h-16 text-white/20"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19V6l12-2v13"
                      />
                    </svg>
                  </div>
                  <p className="text-white/60 italic text-xl">
                    No audio files added yet.
                  </p>
                  <p className="text-white/40 text-sm mt-2 max-w-md text-center">
                    You haven't added any audio files to this soundboard. Check
                    back later for audio samples that represent this brand's
                    sound identity.
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
        {/* Notes */}
        {notes?.length > 0 && (
          <section className="min-h-[40vh] relative overflow-hidden py-20">
            <div className="max-w-7xl mx-auto px-6">
              <h3 className="text-4xl md:text-5xl font-bold mb-12 text-white">
                Notes
              </h3>
              <div className="w-full">
                <div className="grid grid-cols-1 gap-8">
                  {notes.map((note, idx) => (
                    <div
                      key={`note-${idx}`}
                      className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 shadow-lg transition-all duration-300"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xl font-medium text-white">
                          {note.title || `Note ${idx + 1}`}
                        </h4>
                        {note.label && (
                          <span className="px-3 py-1 rounded-full text-sm font-medium bg-[var(--primary-color)]/20 text-[var(--primary-color)]">
                            {note.label}
                          </span>
                        )}
                      </div>
                      <div className="prose prose-lg prose-invert">
                        <p className="text-white/80 text-base leading-relaxed whitespace-pre-wrap">
                          {getNoteContent(idx, note) || "No content provided."}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
        {/* Empty state */}
        {!description &&
          timeline.length === 0 &&
          audioFiles.length === 0 &&
          notes.length === 0 && (
            <div className="text-gray-400 text-center py-8">
              No soundboard data available.
            </div>
          )}
      </div>
    </section>
  );
};

export default DynamicSoundboardSection;
